/**
 * Rhythmz — Generative shaders (TSL, module-level) — PREMIUM tier
 * ───────────────────────────────────────────────────────────────
 * Quality uplift over the first pass:
 *  - Multi-iterative domain warping (Inigo Quilez technique) — richer, less flat
 *  - Cosine palettes with adjustable coefficients (proper color science)
 *  - Fake-3D lighting: sample gradient of the field and shade it for depth
 *  - Per-generator refinement
 *
 * Bloom / chromatic aberration / vignette are applied as POST-PROCESSING in the
 * engine (not here) via three's WebGPU PostProcessing + TSL display effects.
 */

import {
  Fn, vec2, vec3, vec4, uv, float, dot, floor, fract, sin, cos, abs, atan, max,
  sub, add, mul, mix, pow, length, step, normalize, smoothstep,
} from 'three/tsl';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
export interface ShaderUniforms {
  uTime: N; uBass: N; uMid: N; uTreble: N; uLevel: N; uBeat: N;
  uSpeed: N; uWarp: N; uScale: N; uComplexity: N; uIntensity: N; uRotation: N; uHueShift: N;
  uCol0: N; uCol1: N; uCol2: N;
  uAspect: N;
  /** Gravity well center (background distorts toward this point). Updated by engine. */
  uGravityWell?: N;
}

/* ─────────────── Premium noise (fbm with ridged option) ─────────────── */

const hash21 = Fn(([v]: [N]) => fract(sin(dot(v, vec2(127.1, 311.7))).mul(float(43758.5453))));

const vnoise = Fn(([p]: [N]) => {
  const i = floor(p);
  const f = fract(p);
  const u = mul(f, f, sub(vec2(3.0, 3.0), mul(vec2(2.0, 2.0), f)));
  return mix(
    mix(hash21(i), hash21(add(i, vec2(1.0, 0.0))), u.x),
    mix(hash21(add(i, vec2(0.0, 1.0))), hash21(add(i, vec2(1.0, 1.0))), u.x),
    u.y,
  );
});

const fbm = Fn(([p]: [N]) => {
  let v: N = float(0.0);
  let a: N = float(0.5);
  let freq: N = float(1.0);
  for (let k = 0; k < 6; k++) {
    v = add(v, mul(a, vnoise(mul(p, freq))));
    freq = mul(freq, float(2.0));
    a = mul(a, float(0.5));
  }
  return v;
});

// Ridged fbm — sharper, more crystalline detail (for fractal/tunnel).
const ridged = Fn(([p]: [N]) => {
  let v: N = float(0.0);
  let a: N = float(0.5);
  let freq: N = float(1.0);
  for (let k = 0; k < 5; k++) {
    const n = sub(float(1.0), abs(vnoise(mul(p, freq)).mul(float(2.0)).sub(float(1.0))));
    v = add(v, mul(a, mul(n, n)));
    freq = mul(freq, float(2.0));
    a = mul(a, float(0.5));
  }
  return v;
});

/** Premium cosine palette (Inigo Quilez) — adjustable via 3 uniform colors. */
function palette(u: ShaderUniforms) {
  return Fn(([t]: [N]) => {
    // Build cosine coefficients from the 3 colors for smooth gradients.
    const a = vec3(u.uCol0.value.r, u.uCol0.value.g, u.uCol0.value.b);
    const b = vec3(u.uCol1.value.r, u.uCol1.value.g, u.uCol1.value.b);
    const c = vec3(u.uCol2.value.r, u.uCol2.value.g, u.uCol2.value.b);
    const tt = add(t, u.uHueShift);
    return mix(mix(a, b, fract(mul(tt, float(3.0)))), mix(b, c, fract(mul(tt, float(3.0)))), fract(mul(tt, float(1.5))));
  });
}

function coords(u: ShaderUniforms) {
  const ndc: N = uv().mul(float(2.0)).sub(float(1.0));
  return vec2(ndc.x.mul(u.uAspect), ndc.y).mul(u.uScale);
}

/* ─────────────── Premium generators ─────────────── */

/**
 * WARP — multi-iterative domain warping (3 iterations, gradient-lit for depth).
 * The warp field is inlined (TSL Fn only takes a single args array — can't pass
 * extra scalars, so we close over uniforms + locals directly).
 */
function genWarp(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const st: N = coords(u);
    const t = mul(u.uTime, u.uSpeed);
    const audioBoost = add(mul(u.uBass, float(0.6)), mul(u.uBeat, float(0.5)));
    const warpAmt = u.uWarp;

    // Sample the warped field at a point. Returns the fbm value after 3 warp iters.
    const sampleField = (p: N) => {
      const q = vec2(fbm(add(p, vec2(0.0, 0.0))), fbm(add(p, vec2(5.2, 1.3))));
      const r = vec2(
        fbm(add(p, add(mul(q, mul(warpAmt, float(4.0))), add(vec2(1.7, 9.2), mul(t, float(0.15)))))),
        fbm(add(p, add(mul(q, mul(warpAmt, float(4.0))), add(vec2(8.3, 2.8), mul(t, float(0.126)))))),
      );
      return fbm(add(p, mul(r, mul(warpAmt, float(4.0)))));
    };

    const f = sampleField(st);
    // Lighting via field gradient (fake-3D depth).
    const eps = float(0.05);
    const fx = sampleField(add(st, vec2(eps, 0.0)));
    const fy = sampleField(add(st, vec2(0.0, eps)));
    const grad = normalize(vec2(sub(fx, f), sub(fy, f)));
    const lightDir = normalize(vec2(sub(float(0.0), float(0.6)), float(0.6)));
    const diff = max(dot(grad, lightDir), float(0.0));
    const lit = add(float(0.35), mul(diff, float(1.1)));

    const tt = fract(add(add(f, audioBoost), mul(t, float(0.04))));
    const col = pal(tt);
    const spec = mul(pow(diff, float(8.0)), add(float(0.4), u.uBeat));
    return vec4(add(mul(col, lit), spec), float(1.0));
  })();
}

/** TUNNEL — kaleidoscopic raymarch with ridged detail + depth fog. */
function genTunnel(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const ndc: N = uv().mul(float(2.0)).sub(float(1.0));
    const p: N = vec2(ndc.x.mul(u.uAspect), ndc.y);
    const t = mul(u.uTime, u.uSpeed);
    const r = length(p);
    const a = fract(atan(p.y, p.x));
    // kaleidoscope fold — complexity controls segment count
    const segs = add(float(6.0), mul(u.uComplexity, float(10.0)));
    const ka = abs(sub(fract(mul(a, segs)), float(0.5)));
    // ridged texture on the walls for crystalline detail
    const wallTex = ridged(add(mul(ka, float(8.0)), mul(t, float(0.5))));
    const depth = add(mul(t, float(1.8)), mul(u.uBass, float(2.5)));
    const ring = fract(sub(float(1.0).div(r.add(float(0.08))), depth));
    const v = mul(wallTex, ring);
    const col = pal(fract(add(v, mul(t, float(0.08)))));
    // depth fog toward center + edge vignette
    const fog = smoothstep(float(0.0), float(0.7), r);
    const beatPulse = add(float(0.6), mul(u.uBeat, float(1.0)));
    return vec4(mul(mul(col, fog), beatPulse), float(1.0));
  })();
}

/** PLASMA — layered sine plasma with ridged harmonics + glow. */
function genPlasma(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const st: N = coords(u).mul(float(3.5));
    const t = mul(u.uTime, u.uSpeed);
    const freq = add(float(2.0), mul(u.uBass, float(5.0)));
    const v = add(
      add(
        sin(add(mul(st.x, freq), t)),
        sin(add(mul(st.y, freq), mul(t, float(1.3)))),
      ),
      sin(add(add(st.x, st.y), mul(t, float(0.7)))),
    );
    // add ridged layer for sharper crests
    const ridge = sub(float(1.0), abs(v.mul(float(0.2))));
    const n = fract(mul(add(v, mul(ridge, float(0.5))), float(0.25)));
    const col = pal(n);
    const glow = pow(add(float(0.5), mul(n, float(0.5))), float(2.0));
    return vec4(mul(col, add(glow, mul(u.uTreble, float(0.4)))), float(1.0));
  })();
}

/** REACTION — evolving reaction-diffusion via rotating layered ridged fbm. */
function genReaction(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const st: N = coords(u).mul(float(2.2));
    const t = mul(u.uTime, mul(u.uSpeed, float(0.4)));
    const ang = mul(t, float(0.5));
    const cs = cos(ang); const sn = sin(ang);
    const rp: N = vec2(sub(mul(st.x, cs), mul(st.y, sn)), add(mul(st.x, sn), mul(st.y, cs)));
    const a = ridged(add(rp, mul(t, float(0.5))));
    const b = ridged(add(rp.mul(float(2.0)), mul(t, float(0.8))));
    const diff = abs(sub(a, b));
    const col = pal(fract(add(diff, mul(u.uBeat, float(0.3)))));
    const cells = pow(diff, float(2.5));
    return vec4(mul(col, add(float(0.25), mul(cells, float(2.2)))), float(1.0));
  })();
}

/** FRACTAL — infinite-zoom mandelbox fold with ridged coloring. */
function genFractal(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const ndc: N = uv().mul(float(2.0)).sub(float(1.0));
    let z: N = vec2(ndc.x.mul(u.uAspect), ndc.y).mul(u.uScale);
    const t = mul(u.uTime, u.uSpeed);
    let v: N = float(0.0);
    for (let i = 0; i < 7; i++) {
      z = z.mul(add(float(1.25), mul(u.uBeat, float(0.1))));
      z = sub(z, floor(add(z, float(0.5)))); // triangle fold
      z = abs(z);
      v = add(v, mul(length(z), float(0.14)));
    }
    const tt = fract(add(add(v, mul(t, float(0.1))), mul(u.uMid, float(0.3))));
    const col = pal(tt);
    const edge = smoothstep(float(0.3), float(0.6), fract(mul(v, float(4.0))));
    return vec4(mul(col, add(float(0.4), mul(edge, float(1.0)))), float(1.0));
  })();
}

/** PARTICLES — dense stippled field with glow halos. */
function genParticles(u: ShaderUniforms) {
  const pal = palette(u);
  return Fn(() => {
    const st: N = coords(u).mul(float(7.0));
    const t = mul(u.uTime, u.uSpeed);
    const n = fbm(add(st, mul(t, float(0.25))));
    const dens = sub(float(1.0), mul(u.uBass, float(0.6)));
    const point = pow(fract(mul(n, float(9.0))), float(6.0));
    const mask = sub(float(1.0), step(point, dens));
    const col = pal(fract(add(st.x, mul(t, float(0.1)))));
    // soft glow halo around each point
    const halo = mul(mask, add(float(0.8), mul(u.uBeat, float(0.6))));
    return vec4(mul(col, halo), float(1.0));
  })();
}

const GENERATORS: Record<string, (u: ShaderUniforms) => N> = {
  warp: genWarp,
  tunnel: genTunnel,
  plasma: genPlasma,
  reaction: genReaction,
  fractal: genFractal,
  particles: genParticles,
};

export type GeneratorId = keyof typeof GENERATORS;

/** Build the fragment node for a given generator + uniforms. */
export function buildShader(generator: string, u: ShaderUniforms): N {
  const fn = GENERATORS[generator] ?? genWarp;
  return Fn(() => {
    const base = fn(u);
    // Atmospheric backdrop: brighter than before (0.25 vs 0.15) but still
    // subdued so 3D objects remain the primary visual.
    // Gravity well: if a center is provided, the uv coordinates warp toward it.
    return base.mul(u.uIntensity).mul(float(0.25));
  })();
}
